import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { Button, Checkbox, MenuItem, Select, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Pagination from "@mui/material/Pagination";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { deleteLandedCostVendorHistory } from "../../API/landedCostVendorHistoryDeleteApi";
import { updateLandedCostVendorHistory } from "../../API/landedCostVendorHistoryUpdateApi";
import AlertManager from "../../shared/Alert/Alert";
import classes from "./LandedCostUploadHistory.module.css";
import { normalizeCurrencyValue } from "../utils/currency";
import { exportRowsToXlsx } from "../utils/exportUtils";

const percentFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
});

const ISS_COMMISSION_LIMIT = 10000000;

export const normalizeIssCommission = (value) => normalizeCurrencyValue(value);

export const formatIssCommissionDisplay = (value) => {
  const numeric = normalizeIssCommission(value);
  if (numeric === null) {
    return "";
  }
  return `${percentFormatter.format(numeric)}%`;
};

export const normalizeRowIssCommission = (row) => {
  if (!row || !Object.prototype.hasOwnProperty.call(row, "issCommission")) {
    return row;
  }
  const normalizedValue = normalizeIssCommission(row.issCommission);
  if (row.issCommission === normalizedValue) {
    return row;
  }
  return { ...row, issCommission: normalizedValue };
};

const CONTAINER_TYPE_OPTIONS = ["20 FT", "40 FT"];
const normalizeContainerType = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const compact = value.replace(/\s+/g, "").toUpperCase();
  if (compact === "20FT") return "20 FT";
  if (compact === "40FT") return "40 FT";
  return "";
};

const formatDateOnly = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeRowContainerType = (row) => {
  if (!row || !Object.prototype.hasOwnProperty.call(row, "containerType")) {
    return row;
  }
  const normalizedValue = normalizeContainerType(row.containerType);
  if (row.containerType === normalizedValue) {
    return row;
  }
  return { ...row, containerType: normalizedValue };
};

const LandedCostUploadHistoryTable = ({
  uploadHistory = [],
  loading,
  onRefresh,
  enableDelete = true,
}) => {
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const dirtyRowsRef = useRef(new Map());
  const editableFields = useRef([
    "vendor",
    "vendorName",
    "containerType",
    "issCommission",
    "date",
    "ddpFlag",
  ]);


  const reloadAfterAlert = useCallback((alertPromise) => {
    if (alertPromise && typeof alertPromise.finally === "function") {
      alertPromise.finally(() => window.location.reload());
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    if (typeof onRefresh === "function") {
      try {
        await onRefresh();
      } catch (error) {
        console.error("Failed to refresh vendor history", error);
      }
    }
  }, [onRefresh]);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  const handleExport = () => {
    exportRowsToXlsx({
      columns,
      rows,
      fileName: "vendor-upload-history.xlsx",
      sheetName: "Vendor Uploads",
      warningMessage: "No vendor history to export.",
    });
  };

    useEffect(() => {
      const sourceRows = Array.isArray(uploadHistory)
        ? uploadHistory
        : Array.isArray(uploadHistory?.data)
          ? uploadHistory.data
        : Array.isArray(uploadHistory?.rows)
          ? uploadHistory.rows
        : Array.isArray(uploadHistory?.elements)
          ? uploadHistory.elements
        : Array.isArray(uploadHistory?.queryResults)
          ? uploadHistory.queryResults
          : [];

      const normalized = sourceRows.map((row = {}, index) => ({
        id: row.id ?? row.uuid ?? `${row.vendor ?? "row"}-${row.date ?? index}-${index}`,
        vendor: row.vendor ?? row.vendorId ?? "",
        vendorName: row.vendorName ?? row.vendor_name ?? "",
        containerType: normalizeContainerType(
          row.Containertype ?? row.containerType ?? row.container_type ?? ""
        ),
        ddpFlag: row.ddpFlag ?? row.ddpflag ?? row.ddp_flag ?? "",
        issCommission: normalizeIssCommission(
          row.issCommission ?? row.iss_commision ?? row.isscommision ?? ""
        ),
        date: row.date ?? row.uploadedDate ?? row.createdAt ?? "",
      }));

      setRows(normalized);
      setSelectedRows([]);
    }, [uploadHistory]);

    const handleRowSelect = (rowId, checked) => {
      setSelectedRows((prev) =>
        checked ? [...new Set([...prev, rowId])] : prev.filter((id) => id !== rowId)
      );
    };

    const handleSelectAllRows = useCallback(
      (checked) => {
        if (checked) {
          setSelectedRows(rows.map((row) => row.id));
          return;
        }
        setSelectedRows([]);
      },
      [rows]
    );

    const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;
    const isSelectAllIndeterminate = selectedRows.length > 0 && !isAllSelected;

    const handleDeleteRows = async () => {
      if (!selectedRows.length || deleting) return;
      try {
        await AlertManager.confirm(
          `Delete ${selectedRows.length} selected row(s)?`,
          {
            contactSupport: false,
            contactSupportBtn: true,
            closeBtnText: "Delete",
            contactBtnText: "Cancel",
          }
        );
      } catch (err) {
        return;
      }
      const payload = rows.filter((row) => selectedRows.includes(row.id));
      setDeleting(true);
      try {
        const response = await deleteLandedCostVendorHistory(payload);
        if (response) {
          setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
          setSelectedRows([]);
        } else {
          reloadAfterAlert(AlertManager.error("Failed to delete vendor history."));
        }
      } catch (error) {
        console.error("Failed to delete vendor history", error);
        reloadAfterAlert(AlertManager.error("Failed to delete vendor history."));
      } finally {
        setDeleting(false);
      }
    };

    const markRowAsDirty = useCallback((newRow, oldRow) => {
      if (!newRow || !oldRow) return;
      const touched = editableFields.current.some(
        (field) => newRow[field] !== oldRow[field]
      );
      if (touched) {
        dirtyRowsRef.current.set(newRow.id, newRow);
      } else {
        dirtyRowsRef.current.delete(newRow.id);
      }
    }, []);

    const handleProcessRowUpdate = useCallback(
      (newRow, oldRow) => {
        if (!isEditing) {
          return oldRow;
        }
        const normalizedCommission = clampIssCommission(newRow.issCommission);
        if (normalizedCommission === null) {
          if (!isPartialDecimal(newRow.issCommission)) {
            AlertManager.error("ISS commission must be between -10000000 and 10000000.", {
              contactSupport: false,
            });
          }
          return oldRow;
        }
        const nextRow = normalizeRowContainerType(
          normalizeRowIssCommission({ ...newRow, issCommission: normalizedCommission })
        );
        setRows((prev) =>
          prev.map((row) => (row.id === nextRow.id ? nextRow : row))
        );
        markRowAsDirty(nextRow, oldRow);
        return nextRow;
      },
      [isEditing, markRowAsDirty]
    );

    const handleDdpToggle = useCallback(
      (rowId, checked) => {
        if (!isEditing) return;
        setRows((prev) =>
          prev.map((row) => {
            if (row.id !== rowId) return row;
            const updatedRow = { ...row, ddpFlag: checked ? "Y" : "N" };
            markRowAsDirty(updatedRow, row);
            return updatedRow;
          })
        );
      },
      [isEditing, markRowAsDirty]
    );

    const clampIssCommission = (value) => {
      const numeric = normalizeIssCommission(value);
      if (numeric === null || !Number.isFinite(numeric)) return null;
      if (numeric < -ISS_COMMISSION_LIMIT) return -ISS_COMMISSION_LIMIT;
      if (numeric > ISS_COMMISSION_LIMIT) return ISS_COMMISSION_LIMIT;
      return numeric;
    };

    const isPartialDecimal = (value) =>
      typeof value === "string" && /^-?\d*\.?\d*$/.test(value.trim());

    const PercentEditCell = (params) => {
      const { id, field, value } = params;
      const handleChange = (event) => {
        const raw = event.target.value;
        params.api.setEditCellValue({ id, field, value: raw }, event);
      };
      return (
        <input
          type="number"
          step="0.01"
          value={value ?? ""}
          onChange={handleChange}
          style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "6px" }}
          data-testid={`iss-commission-edit-${id}`}
        />
      );
    };

    const ContainerTypeEditCell = (params) => {
      const value = normalizeContainerType(params.value || "");
      const handleChange = (event) => {
        const nextValue = normalizeContainerType(event.target.value);
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: nextValue,
        });
        params.api.stopCellEditMode({ id: params.id, field: params.field });
      };

      return (
        <Select
          size="small"
          fullWidth
          value={value}
          onChange={handleChange}
          data-testid={`container-type-edit-${params.id}`}
        >
          {CONTAINER_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );
    };

    const CustomDateFilterInput = (props) => {
      const { item, applyValue } = props;
      const [selectedDate, setSelectedDate] = useState(
        item.value ? new Date(item.value) : null
      );

      const handleChange = (newDate) => {
        const formattedDate = newDate ? format(newDate, "yyyy-MM-dd") : null;
        setSelectedDate(newDate);
        applyValue({ ...item, value: formattedDate });
      };

      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(newDate) => handleChange(newDate)}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
      );
    };

    const CustomDateRangeFilterInput = (props) => {
      const { item, applyValue } = props;
      const [selectedDateRangeFrom, setSelectedDateRangeFrom] = useState(
        item.value?.from ? new Date(item.value.from) : null
      );
      const [selectedDateRangeTo, setSelectedDateRangeTo] = useState(
        item.value?.to ? new Date(item.value.to) : null
      );

      const handleChange = (newFrom, newTo) => {
        if (newFrom || newTo) {
          applyValue({
            ...item,
            value: {
              from: newFrom ? format(newFrom, "yyyy-MM-dd") : null,
              to: newTo ? format(newTo, "yyyy-MM-dd") : null,
            },
          });
        }
      };

      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <DatePicker
              label="From Date"
              value={selectedDateRangeFrom}
              onChange={(newDate) => {
                setSelectedDateRangeFrom(newDate);
                handleChange(newDate, selectedDateRangeTo);
              }}
              views={["year", "month", "day"]}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
            <DatePicker
              label="To Date"
              value={selectedDateRangeTo}
              onChange={(newDate) => {
                setSelectedDateRangeTo(newDate);
                handleChange(selectedDateRangeFrom, newDate);
              }}
              views={["year", "month", "day"]}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </div>
        </LocalizationProvider>
      );
    };

  const dateFilterOperators = [
    {
      label: "is",
      value: "is",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          const filterDate = new Date(filterItem.value);
          return ({ value }) => {
            const cellDate = new Date(value);
            return (
              cellDate.getFullYear() === filterDate.getFullYear() &&
              cellDate.getMonth() === filterDate.getMonth() &&
              cellDate.getDate() === filterDate.getDate()
            );
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is after",
        value: "is after",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) > new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is before",
        value: "is before",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) < new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "date range",
        value: "date range",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }

          const { from, to } = filterItem.value;
          const fromDate = from ? new Date(from) : null;
          const toDate = to ? new Date(to) : null;

          return ({ value }) => {
            const cellDate = new Date(value);
            if (fromDate && toDate) {
              return cellDate >= fromDate && cellDate <= toDate;
            }
            if (fromDate) return cellDate >= fromDate;
            if (toDate) return cellDate <= toDate;
            return true;
          };
        },
      InputComponent: CustomDateRangeFilterInput,
    },
  ];

  const createSelectFilterOperators = (options) => [
    {
      label: "is",
      value: "is",
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.columnField || !filterItem.value) {
          return null;
        }
        if (filterItem.value === "Both") {
          return () => true;
        }
        const target = String(filterItem.value).toUpperCase();
        return ({ value }) => String(value ?? "").toUpperCase() === target;
      },
      InputComponent: (props) => {
        const { item, applyValue } = props;
        return (
          <TextField
            select
            variant="standard"
            fullWidth
            label="Value"
            value={item.value ?? "Both"}
            onChange={(event) => applyValue({ ...item, value: event.target.value })}
            SelectProps={{ native: true }}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        );
      },
    },
  ];

  const containerTypeFilterOperators = createSelectFilterOperators(["Both", "20 FT", "40 FT"]);
  const ddpFlagFilterOperators = createSelectFilterOperators(["Both", "Y", "N"]);

  const columns = [
      ...(enableDelete
        ? [
            {
              field: "select",
              headerName: "",
              width: 70,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              hideFromExport: true,
              renderHeader: () => (
                <Checkbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={isSelectAllIndeterminate}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => handleSelectAllRows(event.target.checked)}
                  inputProps={{ "aria-label": "Select all rows" }}
                />
              ),
              renderCell: (params) => (
                <Checkbox
                  size="small"
                  checked={selectedRows.includes(params.row.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => handleRowSelect(params.row.id, event.target.checked)}
                  inputProps={{ "aria-label": `Select row ${params.row.id}` }}
                />
              ),
            },
          ]
        : []),
      { field: "vendor", headerName: "Vendor Account #", flex: 1 },
      { field: "vendorName", headerName: "Vendor Name", flex: 1},
      {
        field: "containerType",
        headerName: "Container Type",
        flex: 1,
        editable: true,
        filterOperators: containerTypeFilterOperators,
        renderEditCell: (params) => <ContainerTypeEditCell {...params} />,
      },
      {
        field: "issCommission",
        headerName: "ISS Commission (%)",
        flex: 1,
        editable: true,
        valueFormatter: ({ value }) => formatIssCommissionDisplay(value),
        preProcessEditCellProps: (params) => {
          const raw = params.props?.value;
          const normalized = clampIssCommission(raw);
          const isValid =
            (normalized !== null && normalized >= -ISS_COMMISSION_LIMIT && normalized <= ISS_COMMISSION_LIMIT) || isPartialDecimal(raw);
          return { ...params.props, error: !isValid };
        },
        valueParser: (value, params) => {
          const normalized = clampIssCommission(value);
          if (normalized === null) {
            if (isPartialDecimal(value)) {
              return value;
            }
            return params?.row?.issCommission ?? "";
          }
          return normalized;
        },
        renderEditCell: (params) => <PercentEditCell {...params} />,
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
        editable: false,
        filterOperators: dateFilterOperators,
      },
      {
        field: "ddpFlag",
        headerName: "DDP_Flag",
        flex: 1,
        sortable: false,
        filterOperators: ddpFlagFilterOperators,
        renderCell: (params) => (
          <Checkbox
            size="small"
            checked={params.value === "Y"}
            disabled={!isEditing}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => handleDdpToggle(params.id, event.target.checked)}
            inputProps={{ "aria-label": `DDP flag for row ${params.row.id}` }}
          />
        ),
      },
    ];

    const handleEditToggle = useCallback(async () => {
      if (!isEditing && rows.length === 0) return;

      if (!isEditing) {
        dirtyRowsRef.current.clear();
        setIsEditing(true);
        setSubmitArmed(false);
        return;
      }
      if (saving) return;
      const today = formatDateOnly();
      const payload = Array.from(dirtyRowsRef.current.values()).map((row) => {
        const normalized = normalizeRowContainerType(normalizeRowIssCommission(row));
        return { ...normalized, date: today };
      });
      if (!payload.length) {
        setIsEditing(false);
        setSubmitArmed(false);
        return;
      }
      try {
        await AlertManager.confirm(
          `Save ${payload.length} edited row(s)?`,
          {
            contactSupport: false,
            contactSupportBtn: true,
            closeBtnText: "Save",
            contactBtnText: "Cancel",
          }
        );
      } catch (err) {
        return;
      }

      setSaving(true);
      try {
        const response = await updateLandedCostVendorHistory(payload);
        if (response) {
          dirtyRowsRef.current.clear();
          setIsEditing(false);
          setSubmitArmed(false);
          setSaving(false);
          await refreshHistory();
          return;
        }
        reloadAfterAlert(AlertManager.error("Failed to update vendor history."));
      } catch (error) {
        console.error("Failed to update vendor history", error);
        reloadAfterAlert(AlertManager.error("Failed to update vendor history."));
      } finally {
        setSaving(false);
      }
    }, [isEditing, rows.length, saving, reloadAfterAlert, refreshHistory]);

  const handleDummySubmit = useCallback(() => {
    setSubmitArmed(true);
  }, []);

  const CustomFooter = () => {
    const countText =
      enableDelete && selectedRows.length > 0
        ? `${selectedRows.length} row${selectedRows.length === 1 ? "" : "s"} selected`
        : "\u00A0";
    return (
      <GridFooterContainer className={classes.footer}>
        <div className={classes.footerSelection}>{countText}</div>
        <CustomPagination />
      </GridFooterContainer>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tableActions}>
        <Button
          variant="contained"
          size="small"
          onClick={handleEditToggle}
          disabled={saving || (isEditing && !submitArmed) || rows.length === 0}
          className={classes.editButton}
        >
          {saving ? "Saving..." : isEditing ? "Save Changes" : "Edit"}
        </Button>
        {isEditing && (
          <Button
            type="submit"
            size="small"
            variant="text"
            className={classes.dummySubmitBtn}
            data-testid="vendor-upload-dummy-submit"
            onClick={handleDummySubmit}
          >
            Finish Editing
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={!rows.length || isEditing || saving}
          className={classes.exportButton}
          startIcon={<DownloadIcon fontSize="small" />}
        >
          Export
        </Button>
        {enableDelete && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeleteRows}
            disabled={deleting || !selectedRows.length || isEditing || rows.length === 0}
            className={classes.deleteButton}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        )}
      </div>
      
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        editMode="cell"
        isCellEditable={() => isEditing}
        processRowUpdate={handleProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        onProcessRowUpdateError={(error) => console.error(error)}
        rowHeight={32}
        columnHeaderHeight={36}
        pagination
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[15, 25, 50]}
        autoHeight
        components={{ Footer: CustomFooter }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
            py: 0.25,
          },
          "& .MuiDataGrid-columnHeaders": {
            fontSize: "0.82rem",
          },
          "& .MuiDataGrid-columnHeadersInner": {
            minHeight: 36,
          },
          "& .MuiDataGrid-row": {
            minHeight: 32,
          },
        }}
        loading={loading || deleting || saving}
      />
    </div>
  );
  };

  export default LandedCostUploadHistoryTable;
